import { useState } from "react"
import { format, parseISO } from "date-fns"
import { fr } from "date-fns/locale"
import { QueryRender } from "@/components/helpers/QueryRender"
import {
   useGlobalSettings,
   useUpdateGlobalSettings,
} from "@/entities/globalSettings/useGlobalSettings"
import { useCurrentSchoolYear } from "@/entities/schoolYear/useSchoolYear"
import { useProcess } from "@/contexts/ProcessContext"
import { mainDocumentId } from "@/entities/globalSettings/globalSettings"
import { InlineEditableProperty } from "@/components/form/InlineEditableProperty"
import { z } from "zod"
import { useUpdateSchoolYear } from "@/entities/schoolYear/useSchoolYear"
import { SchoolYear, schoolYearSchema } from "@/entities/schoolYear/schoolYear"
import { Link } from "react-router-dom"
import { ConfirmDialog } from "@/components/ui/confirmDialog"

const groupSchema = z.string().min(1, "Le nom du groupe est requis")

export default function ParametresApplication() {
   const globalSettingsQuery = useGlobalSettings()
   const currentSchoolYearQuery = useCurrentSchoolYear()
   const updateGlobalSettings = useUpdateGlobalSettings()
   const updateSchoolYear = useUpdateSchoolYear()
   const { startProcess } = useProcess()
   const [newGroup, setNewGroup] = useState("")
   const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false)
   const [periodToDelete, setPeriodToDelete] = useState<{ index: number; schoolYear: SchoolYear } | null>(null)

   const handleAddGroup = async (currentGroups: string[]) => {
      if (!newGroup.trim()) return

      startProcess(async () => {
         await updateGlobalSettings(
            mainDocumentId,
            {
               groups: [...currentGroups, newGroup.trim()],
            },
            {
               quickMode: true,
            },
         )
         setNewGroup("")
      })
   }

   const handleRemoveGroup = async (currentGroups: string[], groupToRemove: string) => {
      startProcess(async () => {
         await updateGlobalSettings(
            mainDocumentId,
            {
               groups: currentGroups.filter((group) => group !== groupToRemove),
            },
            {
               quickMode: true,
            },
         )
      })
   }

   const handleUpdateGroup = async (
      currentGroups: string[],
      oldGroup: string,
      newGroupName: string,
   ) => {
      startProcess(async () => {
         const updatedGroups = currentGroups.map((group) =>
            group === oldGroup ? newGroupName : group,
         )
         await updateGlobalSettings(
            mainDocumentId,
            {
               groups: updatedGroups,
            },
            {
               quickMode: true,
            },
         )
      })
   }

   const handleUpdateSchoolYear = async (field: string, value: string | Date, schoolYear: any) => {
      startProcess(async () => {
         let parsedValue = value as Date
         if (field === "start_date" || field === "end_date") {
            parsedValue = parseISO(value as string)
         }
         await updateSchoolYear(schoolYear.id, {
            [field]: parsedValue,
         }, {
            quickMode: true,
         })
      })
   }

   const handleUpdateVacationPeriod = async (
      index: number,
      field: string,
      value: string | Date,
      schoolYear: any,
   ) => {
      startProcess(async () => {
         const updatedPeriods = [...schoolYear.vacation_periods]
         let parsedValue = value as Date
         if (field === "start_date" || field === "end_date") {
            parsedValue = parseISO(value as string)
         }
         updatedPeriods[index] = {
            ...updatedPeriods[index],
            [field]: parsedValue,
         }
         await updateSchoolYear(schoolYear.id, {
            vacation_periods: updatedPeriods,
         },{
            quickMode: true,
         })
      })
   }

   const handleAddVacationPeriod = async (schoolYear: any) => {
      startProcess(async () => {
         const { start_date, end_date } = schoolYear
         const newPeriod = {
            name: "Nouvelle période",
            start_date,
            end_date,
         }
         await updateSchoolYear(schoolYear.id, {
            vacation_periods: [...schoolYear.vacation_periods, newPeriod],
         }, {
            quickMode: true,
         })
      })
   }

   const handleDeleteVacationPeriod = async (index: number, schoolYear: SchoolYear) => {
      setPeriodToDelete({ index, schoolYear })
      setShowDeleteConfirmDialog(true)
   }

   const confirmDeleteVacationPeriod = async () => {
      if (!periodToDelete) return

      startProcess(async () => {
         const { index, schoolYear } = periodToDelete
         const updatedPeriods = [...schoolYear.vacation_periods]
         updatedPeriods.splice(index, 1)
         await updateSchoolYear(schoolYear.id, {
            vacation_periods: updatedPeriods,
         }, {
            quickMode: true,
         })
      })
      setShowDeleteConfirmDialog(false)
      setPeriodToDelete(null)
   }

   return (
      <>
      <div className="page-section">
         <h1 className="title">Paramètres de l'application</h1>

         <div className="flex flex-col space-y-8">
            <section className="card bg-base-100 shadow">
               <div className="card-body">
                  <h2 className="card-title">Année scolaire actuelle</h2>
                  <QueryRender query={currentSchoolYearQuery}>
                     {(schoolYear) => (
                        <div className="flex flex-col gap-2">
                           <InlineEditableProperty
                              label="Nom"
                              value={schoolYear.name}
                              field="name"
                              schema={schoolYearSchema.shape.name}
                              onSave={async (_, value) =>
                                 handleUpdateSchoolYear("name", value, schoolYear)
                              }
                           />
                           <div className="flex flex-col md:flex-row   gap-4">
                              <InlineEditableProperty
                                 label="Date de début"
                                 value={format(schoolYear.start_date, "dd/MM/yyyy", { locale: fr })}
                                 field="start_date"
                                 type="date"
                                 schema={z.string()}
                                 onSave={async (_, value) =>
                                    handleUpdateSchoolYear("start_date", value, schoolYear)
                                 }
                              />
                              <InlineEditableProperty
                                 label="Date de fin"
                                 value={format(schoolYear.end_date, "dd/MM/yyyy", { locale: fr })}
                                 field="end_date"
                                 type="date"
                                 schema={z.string()}
                                 onSave={async (_, value) =>
                                    handleUpdateSchoolYear("end_date", value, schoolYear)
                                 }
                              />
                           </div>
                           <div className="flex flex-col gap-8 mt-2">
                              <div className="divider">Périodes de vacances</div>
                              {schoolYear.vacation_periods.map((period, index) => (
                                 <div
                                    key={period.name}
                                    className="p-4 bg-white rounded-lg shadow-sm"
                                 >
                                    <div className="flex flex-col gap-4 md:flex-row md:items-center ">
                                       <div className="flex-1 flex flex-col gap-4 md:flex-row md:items-end">
                                          <InlineEditableProperty
                                             label="Nom"
                                             value={period.name}
                                             field={`vacation_${index}_name`}
                                             schema={
                                                schoolYearSchema.shape.vacation_periods.element.shape
                                                   .name
                                             }
                                             onSave={async (_, value) =>
                                                handleUpdateVacationPeriod(
                                                   index,
                                                   "name",
                                                   value,
                                                   schoolYear,
                                                )
                                             }
                                          />
                                          <InlineEditableProperty
                                             label="Début"
                                             value={format(period.start_date, "dd/MM/yyyy", {
                                                locale: fr,
                                             })}
                                             field={`vacation_${index}_start`}
                                             type="date"
                                             schema={z.string()}
                                             onSave={async (_, value) =>
                                                handleUpdateVacationPeriod(
                                                   index,
                                                   "start_date",
                                                   value,
                                                   schoolYear,
                                                )
                                             }
                                          />
                                          <InlineEditableProperty
                                             label="Fin"
                                             value={format(period.end_date, "dd/MM/yyyy", {
                                                locale: fr,
                                             })}
                                             field={`vacation_${index}_end`}
                                             type="date"
                                             schema={z.string()}
                                             onSave={async (_, value) =>
                                                handleUpdateVacationPeriod(
                                                   index,
                                                   "end_date",
                                                   value,
                                                   schoolYear,
                                                )
                                             }
                                          />
                                       </div>
                                       <button
                                          className="btn btn-ghost btn-sm text-error"
                                          onClick={() => handleDeleteVacationPeriod(index, schoolYear)}
                                       >
                                          <svg
                                             xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             strokeWidth={1.5}
                                             stroke="currentColor"
                                             className="w-5 h-5"
                                          >
                                             <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                             />
                                          </svg>
                                       </button>
                                    </div>
                                 </div>
                              ))}
                              <button 
                                 className="btn mt-4"
                                 onClick={() => handleAddVacationPeriod(schoolYear)}
                              >
                                 Ajouter une période de vacances
                              </button>
                           </div>
                        </div>
                     )}
                  </QueryRender>
               </div>
            </section>

            <section className="card bg-base-100 shadow">
               <div className="card-body">
                  <h2 className="card-title">Horaires</h2>
                  <QueryRender query={globalSettingsQuery}>
                     {(settings) => (
                        <div className="flex flex-col gap-4">
                           <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 w-full md:w-1/3">
                                 <InlineEditableProperty
                                    label="Début de jour"
                                    type="time"
                                    value={settings.start_of_day}
                                    field="start_of_day"
                                    onSave={(_field, value) =>
                                       updateGlobalSettings(settings.id, { start_of_day: value })
                                    }
                                 />
                              </div>
                              <div className="flex items-center gap-2 w-full md:w-1/3">
                                 <InlineEditableProperty
                                    label="Pause du matin"
                                    type="time"
                                    value={settings.morning_break}
                                    field="morning_break"
                                    onSave={(_field, value) =>
                                       updateGlobalSettings(settings.id, { morning_break: value })
                                    }
                                 />
                              </div>
                              <div className="flex items-center gap-2 w-full md:w-1/3">
                                 <InlineEditableProperty
                                    label="Pause du midi"
                                    type="time"
                                    value={settings.lunch_break}
                                    field="lunch_break"
                                    onSave={(_field, value) =>
                                       updateGlobalSettings(settings.id, { lunch_break: value })
                                    }
                                 />
                              </div>
                              <div className="flex items-center gap-2 w-full md:w-1/3">
                                 <InlineEditableProperty
                                    label="Pause de l'après-midi"
                                    type="time"
                                    value={settings.afternoon_break}
                                    field="afternoon_break"
                                    onSave={(_field, value) =>
                                       updateGlobalSettings(settings.id, { afternoon_break: value })
                                    }
                                 />
                              </div>
                              <div className="flex items-center gap-2 w-full md:w-1/3">
                                 <InlineEditableProperty
                                    label="Fin de journée"
                                    type="time"
                                    value={settings.end_of_day}
                                    field="end_of_day"
                                    onSave={(_field, value) =>
                                       updateGlobalSettings(settings.id, { end_of_day: value })
                                    }
                                 />
                              </div>
                           </div>
                        </div>
                     )}
                  </QueryRender>
               </div>
            </section>

            <section className="card bg-base-100 shadow">
               <div className="card-body">
                  <h2 className="card-title">Groupes</h2>
                  <QueryRender query={globalSettingsQuery}>
                     {(settings) => (
                        <div className="flex flex-col gap-4">
                           <div className="flex flex-col gap-2">
                              {settings.groups.map((group) => (
                                 <div
                                    key={group}
                                    className="flex  items-center gap-2 w-full md:w-1/3"
                                 >
                                    <InlineEditableProperty
                                       label=""
                                       value={group}
                                       field={`group_${group}`}
                                       schema={groupSchema}
                                       onSave={async (_, newValue) => {
                                          await handleUpdateGroup(settings.groups, group, newValue)
                                       }}
                                       onDelete={() => handleRemoveGroup(settings.groups, group)}
                                    />
                                 </div>
                              ))}
                           </div>

                           <div className="flex gap-2">
                              <input
                                 type="text"
                                 placeholder="Nouveau groupe"
                                 className="input input-bordered max-w-[60%] md:max-w-auto"
                                 value={newGroup}
                                 onChange={(e) => setNewGroup(e.target.value)}
                                 onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                       handleAddGroup(settings.groups)
                                    }
                                 }}
                              />
                              <button
                                 onClick={() => handleAddGroup(settings.groups)}
                                 className="btn  "
                              >
                                 <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                 >
                                    <path
                                       fillRule="evenodd"
                                       d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                       clipRule="evenodd"
                                    />
                                 </svg>
                                 Ajouter
                              </button>
                           </div>
                        </div>
                     )}
                  </QueryRender>
               </div>
            </section>
         </div>

         <div className="flex flex-col md:flex-row gap-2 md:gap-4 mt-8">
            <Link
               to="/admin/nouvelle-annee/introduction"
               className="btn btn-primary _p-3 text-base  "
            >
               <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6"
               >
                  <path
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                  />
               </svg>
               Démarrer nouvelle année
            </Link>
            <Link to="/admin/nouvelle-annee/configurer-groupes" className="btn _p-3 text-base ">
               <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6"
               >
                  <path
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"
                  />
               </svg>
               Réorganiser les groupes
            </Link>
         </div>
      </div>
   
      {showDeleteConfirmDialog && periodToDelete && (
         <ConfirmDialog
            message="Êtes-vous sûr de vouloir supprimer cette période de vacances ?"
            onConfirm={confirmDeleteVacationPeriod}
            onCancel={() => {
               setShowDeleteConfirmDialog(false)
               setPeriodToDelete(null)
            }}
         />
      )}
   </>)
}
